(function () {
  'use strict';
  const theme = window.theme || {};

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-media-target]');
        if (!thumb) return;
        this.show(thumb.dataset.mediaTarget);
      });
      document.addEventListener('variant:changed', (e) => {
        const mediaId = e.detail.variant && e.detail.variant.featured_media
          ? e.detail.variant.featured_media.id
          : null;
        if (mediaId) this.show(String(mediaId));
      });
    }

    show(mediaId) {
      this.querySelectorAll('[data-media-id]').forEach((item) => {
        const match = item.dataset.mediaId === String(mediaId);
        item.classList.toggle('is-active', match);
        if (match) item.removeAttribute('data-hidden');
        else item.setAttribute('data-hidden', '');
        const video = item.querySelector('video');
        if (video && !match) video.pause();
      });
      this.querySelectorAll('[data-media-target]').forEach((thumb) => {
        const match = thumb.dataset.mediaTarget === String(mediaId);
        thumb.classList.toggle('is-active', match);
        if (match) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variant-data]');
      if (!dataEl) return;
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (err) {
        return;
      }
      this.selects = Array.from(this.querySelectorAll('select'));
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability(this.selectedOptions);
    }

    get selectedOptions() {
      return this.selects.map((s) => s.value);
    }

    onChange() {
      const chosen = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === chosen[i])
      );

      this.updateAvailability(chosen);

      if (!variant) {
        this.currentVariantId = null;
        if (this.priceController) this.priceController.abort();
        this.setUnavailable();
        return;
      }

      this.currentVariantId = variant.id;
      this.setAvailable();
      this.updateUrl(variant);
      this.updateFormId(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateQuantityLimit(variant);
      this.announce(variant);

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
    }

    updateAvailability(chosen) {
      this.selects.forEach((select, index) => {
        Array.from(select.options).forEach((option) => {
          const candidate = chosen.slice();
          candidate[index] = option.value;

          const exists = this.variants.some((v) => v.options.every((opt, i) => opt === candidate[i]));
          const inStock = this.variants.some(
            (v) => v.available && v.options.every((opt, i) => opt === candidate[i])
          );

          let suffix = '';
          if (!exists) suffix = ' (unavailable)';
          else if (!inStock) suffix = ' (sold out)';

          option.textContent = option.value + suffix;
          option.dataset.state = !exists ? 'missing' : inStock ? 'available' : 'sold-out';
        });
      });
    }

    updateQuantityLimit(variant) {
      if (!('inventory_management' in variant) || !('inventory_quantity' in variant)) return;
      const tracked = variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny';
      this.applyQuantityLimit(tracked && variant.inventory_quantity > 0 ? variant.inventory_quantity : null);
    }

    applyQuantityLimit(max) {
      const input = document.querySelector('.pdp__buy-row .qty__input');
      if (!input) return;
      if (max) {
        input.max = max;
        input.dataset.max = max;
      } else {
        input.removeAttribute('max');
        delete input.dataset.max;
      }
      if (Number(input.value) > (Number(input.dataset.max) || Infinity)) input.value = input.dataset.max;
      const stepper = input.closest('quantity-input');
      if (stepper && typeof stepper.setValue === 'function') stepper.setValue(Number(input.value));
    }

    announce(variant) {
      const region = document.getElementById('A11yStatus');
      if (!region) return;
      const price =
        typeof window.MaisonFormatMoney === 'function'
          ? window.MaisonFormatMoney(variant.price)
          : (variant.price / 100).toFixed(2);
      region.textContent = `${variant.title}. ${variant.available ? price : theme.strings.soldOut || 'Sold out'}`;
    }

    updateUrl(variant) {
      if (!variant || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    updateFormId(variant) {
      document.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant.id;
      });
    }

    updatePrice(variant) {
      const container = document.getElementById(`Price-${this.dataset.section}`);
      if (!container) return;
      if (this.priceController) this.priceController.abort();
      this.priceController = typeof AbortController === 'function' ? new AbortController() : null;
      const requested = variant.id;
      fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`, {
        signal: this.priceController ? this.priceController.signal : undefined
      })
        .then((r) => r.text())
        .then((html) => {
          if (this.currentVariantId !== requested) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.getElementById(`Price-${this.dataset.section}`);
          if (fresh) container.innerHTML = fresh.innerHTML;
          const stock = parsed.querySelector('[data-inventory]');
          const currentStock = document.querySelector('[data-inventory]');
          if (stock && currentStock) currentStock.innerHTML = stock.innerHTML;
          const freshQty = parsed.querySelector('.pdp__buy-row .qty__input');
          if (freshQty) this.applyQuantityLimit(freshQty.dataset.max ? Number(freshQty.dataset.max) : null);
        })
        .catch(() => {});
    }

    updateButton(variant) {
      const button = document.querySelector('[data-add-button]');
      if (!button) return;
      const label = button.querySelector('.btn__label');
      button.disabled = !variant.available;
      if (label) {
        label.textContent = variant.available
          ? theme.strings.addToCart || 'Add to basket'
          : theme.strings.soldOut || 'Sold out';
      }
    }

    setUnavailable() {
      const form = document.getElementById('ProductForm');
      if (form) form.dataset.unavailable = 'true';

      const button = document.querySelector('[data-add-button]');
      if (button) {
        button.disabled = true;
        const label = button.querySelector('.btn__label');
        if (label) label.textContent = theme.strings.unavailable || 'Unavailable';
      }
      document.querySelectorAll('.shopify-payment-button').forEach((el) => {
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
      });
      this.announceUnavailable();
    }

    setAvailable() {
      const form = document.getElementById('ProductForm');
      if (form) delete form.dataset.unavailable;
      document.querySelectorAll('.shopify-payment-button').forEach((el) => {
        el.removeAttribute('hidden');
        el.removeAttribute('aria-hidden');
      });
    }

    announceUnavailable() {
      const region = document.getElementById('A11yStatus');
      if (region) region.textContent = theme.strings.unavailable || 'That combination is unavailable';
    }
  }
  customElements.define('variant-selects', VariantSelects);

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      if (!button) return;
      const url = this.dataset.url || window.location.href;

      button.addEventListener('click', async () => {
        if (navigator.share) {
          try {
            await navigator.share({ title: document.title, url });
            return;
          } catch (err) {
            if (err.name === 'AbortError') return;
          }
        }
        try {
          await navigator.clipboard.writeText(url);
          if (button.dataset.copied) return;
          const original = button.textContent;
          button.dataset.copied = 'true';
          button.textContent = (theme.strings && theme.strings.copied) || 'Link copied';
          setTimeout(() => { button.textContent = original; delete button.dataset.copied; }, 2200);
        } catch (err) {
          window.prompt('Copy this link', url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);
})();
